import { sendToApi } from 'api/client'
import { AlmanacFileShowResponse, AlmanacFilesIndexResponse } from 'hooks/queries/files/types'
import qs from 'qs'
import { PostStatusPropertyValue } from './PropertyClient'
import { PostStatusV2 } from 'screens/ScheduledPosts/Filters/StatusMenuV2'

export type RansackQ = Record<string, string | string[] | number[] | Date | boolean>

interface IndexParams {
  page?: number
  items?: number
  q?: RansackQ
  project_key?: string
  partial_calendar?: boolean
}

interface ShowParams {
  q?: RansackQ
  partial_calendar?: boolean
}

export interface UpdateAlmanacFileInput {
  title?: string | null
  post_at?: string | null
  post_status?: string | null
  view_metadata?: {
    view: 'calendar_view' | 'list_view'
  }
  reviewed_at?: string | null
}

export const PROPERTY_STATUS_TO_COLUMN_VALUE: Record<PostStatusPropertyValue, number> = {
  [PostStatusPropertyValue.Draft]: 0,
  [PostStatusPropertyValue.ReminderScheduled]: 1,
  [PostStatusPropertyValue.AutoPostScheduled]: 2,
  [PostStatusPropertyValue.Posted]: 3,
  [PostStatusPropertyValue.Failed]: 4,
}

// "Approved", "Review" and "Scheduled" are pseudo-statuses exposed to users in
// the StatusMenu but are not real post_status values — Approved/Review map to
// whether the almanac file's `reviewed_at` column is set, and Scheduled is the
// catch-all bucket for workspaces with post_approval turned off.
export const APPROVED_PSEUDO_STATUS = PostStatusV2.Approved
export const REVIEW_PSEUDO_STATUS = PostStatusV2.Review
export const SCHEDULED_PSEUDO_STATUS = PostStatusV2.Scheduled
export type PseudoStatus =
  | typeof APPROVED_PSEUDO_STATUS
  | typeof REVIEW_PSEUDO_STATUS
  | typeof SCHEDULED_PSEUDO_STATUS
export const PSEUDO_STATUSES: PseudoStatus[] = [
  APPROVED_PSEUDO_STATUS,
  REVIEW_PSEUDO_STATUS,
  SCHEDULED_PSEUDO_STATUS,
]
// Backwards-compatible alias for callers that still reference the old name.
export type ReviewPseudoStatus = PseudoStatus
export const REVIEW_PSEUDO_STATUSES = PSEUDO_STATUSES

export type Sorting =
  | 'post_at desc'
  | 'post_at asc'
  | 'title desc'
  | 'title asc'
  | 'post_status desc'
  | 'post_status asc'
  | 'created_at desc'
  | 'created_at asc'

export const buildFilesQuery = ({
  channels,
  contentTypeCategories,
  statuses,
  externalIntegrationIds,
  projectKeys,
  folderId,
  sorting,
  startDate,
  endDate,
  campaignId,
  campaignIds,
  needsApproval,
  failedSince,
  approved,
  needsReview,
  useStatusFilterScope = false,
}: {
  channels?: string[]
  contentTypeCategories?: string[]
  statuses?: string[]
  externalIntegrationIds?: number[]
  projectKeys?: string[]
  folderId?: string
  sorting?: Sorting
  startDate?: string | null
  endDate?: string
  campaignId?: number
  campaignIds?: string[]
  needsApproval?: boolean
  failedSince?: string | null
  approved?: boolean | null
  needsReview?: boolean | null
  /**
   * Opt-in to the V2 status filter on the backend. When true, all status
   * filtering (real post_status values + Approved/Review pseudo-statuses)
   * is sent as a single `status_filter` ransack scope which OR's everything
   * together server-side. When false (default), real statuses are sent via
   * the legacy `post_status_in` ransack predicate for backwards compatibility.
   * Use true for callers driven by StatusMenuV2.
   */
  useStatusFilterScope?: boolean
}) => {
  const query: RansackQ = {}

  // Build the value list for the backend's `status_filter` scope, which
  // combines real post_status integers with the 'Approved'/'Review'/'Scheduled'
  // pseudo-statuses using OR semantics on the server side. The explicit
  // `approved` / `needsReview` boolean args are also folded in here for
  // callers that don't go through the StatusMenu.
  const statusFilterValues: (number | string)[] = []
  for (const status of statuses ?? []) {
    if ((PSEUDO_STATUSES as string[]).includes(status)) {
      statusFilterValues.push(status)
    } else {
      const intValue = PROPERTY_STATUS_TO_COLUMN_VALUE[status as PostStatusPropertyValue]
      if (intValue !== undefined) statusFilterValues.push(intValue)
    }
  }
  if (approved && !statusFilterValues.includes(APPROVED_PSEUDO_STATUS)) {
    statusFilterValues.push(APPROVED_PSEUDO_STATUS)
  }
  if (needsReview && !statusFilterValues.includes(REVIEW_PSEUDO_STATUS)) {
    statusFilterValues.push(REVIEW_PSEUDO_STATUS)
  }

  if (startDate) {
    query.post_at_gteq = startDate
  }
  if (endDate) {
    query.post_at_lteq = endDate
  }
  if (channels && channels.length > 0) {
    query.channel_in = channels
  }
  if (contentTypeCategories && contentTypeCategories.length > 0) {
    query.content_type_filter = contentTypeCategories
  }

  // Status filtering: either the legacy `post_status_in` ransack predicate
  // (AND with everything else, no Approved/Review support) or the new
  // `status_filter` ransack scope (OR semantics, handles pseudo-statuses).
  // Caller opts in to the new scope via `useStatusFilterScope` — typically
  // when driven by StatusMenuV2.
  if (useStatusFilterScope) {
    if (statusFilterValues.length > 0) {
      query.status_filter = statusFilterValues.map((v) => String(v))
    }
  } else if (statuses && statuses.length > 0) {
    query.post_status_in = statuses
      .map((status) => PROPERTY_STATUS_TO_COLUMN_VALUE[status as PostStatusPropertyValue])
      .filter((v): v is number => v !== undefined)
  }
  if (externalIntegrationIds && externalIntegrationIds.length > 0) {
    query.external_integration_id_in = externalIntegrationIds
  }
  if (folderId) {
    query.by_project = folderId
  }
  if (projectKeys && projectKeys.length > 0) {
    query.project_key_in = projectKeys
  }
  if (sorting) {
    query.s = sorting
  }
  if (campaignId) {
    query.campaign_id_eq = campaignId.toString()
  }
  if (campaignIds && campaignIds.length > 0) {
    query.campaign_id_in = campaignIds
  }
  if (needsApproval) {
    query.needs_approval = 'true'
  }
  if (failedSince) {
    query.failed_since = failedSince
  }

  return query
}

export const AlmanacFilesClient = {
  index: async (workspaceId: number, params: IndexParams = {}) => {
    return sendToApi<AlmanacFilesIndexResponse>(`/w/${workspaceId}/almanac_files`, {
      method: 'GET',
      params,
      stringify: (object) => qs.stringify(object, { arrayFormat: 'brackets' }),
    })
  },
  recents: async (workspaceId: number, params: IndexParams = {}) => {
    return sendToApi<AlmanacFilesIndexResponse>(`/w/${workspaceId}/almanac_files/recents`, {
      method: 'GET',
      params,
      stringify: (object) => qs.stringify(object, { arrayFormat: 'brackets' }),
    })
  },
  show: async (workspaceId: number, key: string, params?: ShowParams) => {
    return sendToApi<AlmanacFileShowResponse>(`/w/${workspaceId}/almanac_files/${key}`, {
      method: 'GET',
      params,
      stringify: (object) => qs.stringify(object, { arrayFormat: 'brackets' }),
    })
  },
  update: async (workspaceId: number, key: string, payload: UpdateAlmanacFileInput) => {
    return sendToApi<AlmanacFileShowResponse>(`/w/${workspaceId}/almanac_files/${key}`, {
      method: 'PUT',
      body: { almanac_file: payload },
    })
  },
}
